/* ==========================================================================
   Royal Care Professional — rail carousel + tabs

   One controller for every rc-* section that needs a swipeable row of cards.
   The rail itself is CSS scroll-snap, so with JS disabled the row is still a
   working touch carousel; this file only adds the arrow buttons, keeps their
   disabled state truthful, and switches tab panels.

   No dependencies, no rAF loop while idle: the scroll listener is passive and
   coalesced into a single frame, and it only reads two numbers.

   Idempotent — init() claims nodes once, so the theme editor can re-run it on
   every section load.
   ========================================================================== */

(function () {
  'use strict';

  if (window.RCCarousel) {
    window.RCCarousel.init();
    return;
  }

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function smooth() {
    return window.matchMedia(REDUCED).matches ? 'auto' : 'smooth';
  }

  /* ---- rail ---------------------------------------------------------- */

  function Rail(root) {
    this.root = root;
    this.track = root.querySelector('[data-rc-track]');
    this.prev = root.querySelector('[data-rc-prev]');
    this.next = root.querySelector('[data-rc-next]');
    this.frame = 0;

    this.sync = this.sync.bind(this);
    this.queue = this.queue.bind(this);
  }

  /** One card plus its gap — the distance a single arrow press travels. */
  Rail.prototype.step = function () {
    var first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;

    var styles = window.getComputedStyle(this.track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;

    return Math.round(first.getBoundingClientRect().width + gap);
  };

  Rail.prototype.go = function (direction) {
    this.track.scrollBy({ left: this.step() * direction, behavior: smooth() });
  };

  Rail.prototype.queue = function () {
    if (this.frame) return;
    this.frame = requestAnimationFrame(this.sync);
  };

  /**
   * Paint the arrows from the current scroll offset. The 2px slack absorbs the
   * sub-pixel scrollLeft browsers report at the extremes.
   */
  Rail.prototype.sync = function () {
    this.frame = 0;

    var track = this.track;
    var travel = track.scrollWidth - track.clientWidth;
    var idle = travel <= 2;

    // Everything fits: no arrows, no fades, plain grid.
    this.root.classList.toggle('is-static', idle);

    if (this.prev) this.prev.disabled = idle || track.scrollLeft <= 2;
    if (this.next) this.next.disabled = idle || track.scrollLeft >= travel - 2;
  };

  Rail.prototype.mount = function () {
    var self = this;
    if (!this.track) return;

    if (this.prev) {
      this.prev.addEventListener('click', function () {
        self.go(-1);
      });
    }

    if (this.next) {
      this.next.addEventListener('click', function () {
        self.go(1);
      });
    }

    this.track.addEventListener('scroll', this.queue, { passive: true });

    // No sync() here. Reading the track during start-up forced a layout of
    // the whole page for every rail; the observer's first callback arrives
    // once the rail has been laid out, when the same read costs nothing.
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.queue);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.queue);
      this.queue();
    }
  };

  /* ---- tabs ---------------------------------------------------------- */

  function Tabs(root) {
    this.root = root;
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-rc-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-rc-panel]'));
    this.index = 0;
  }

  Tabs.prototype.show = function (next, focus) {
    if (!this.tabs[next]) return;
    this.index = next;

    this.tabs.forEach(function (tab, i) {
      var on = i === next;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
      // Roving tabindex: the tablist is one tab stop, arrows move inside it.
      tab.tabIndex = on ? 0 : -1;
    });

    this.panels.forEach(function (panel, i) {
      var on = i === next;
      panel.hidden = !on;
      panel.classList.toggle('is-active', on);

      // A hidden panel measures 0 wide, so its rail could not size its arrows
      // until now. Re-sync the moment it becomes visible.
      if (on) {
        Array.prototype.forEach.call(panel.querySelectorAll('[data-rc-carousel]'), function (node) {
          if (node.rcRail) node.rcRail.queue();
        });
      }
    });

    if (focus) this.tabs[next].focus();
  };

  Tabs.prototype.mount = function () {
    var self = this;
    if (this.tabs.length < 2) return;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.show(i);
      });

      tab.addEventListener('keydown', function (event) {
        var last = self.tabs.length - 1;
        var to = -1;

        if (event.key === 'ArrowRight') to = i === last ? 0 : i + 1;
        else if (event.key === 'ArrowLeft') to = i === 0 ? last : i - 1;
        else if (event.key === 'Home') to = 0;
        else if (event.key === 'End') to = last;

        if (to === -1) return;
        event.preventDefault();
        self.show(to, true);
      });
    });

    this.show(0);
  };

  /* ---- boot ---------------------------------------------------------- */

  function init(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(root.querySelectorAll('[data-rc-carousel]'), function (node) {
      if (node.rcRail) return;
      var rail = new Rail(node);
      node.rcRail = rail;
      rail.mount();
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-rc-tabs]'), function (node) {
      if (node.rcTabs) return;
      var tabs = new Tabs(node);
      node.rcTabs = tabs;
      tabs.mount();
    });
  }

  window.RCCarousel = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
